import React, { useCallback, useEffect, useState } from 'react';
import CardShell from '@/components/CardShell';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';
import CtaButton from '@/components/CtaButton';
import VerifyScreen from '@/screens/VerifyScreen';
import DeadLinkScreen from '@/screens/DeadLinkScreen';
import RequestLinkScreen from '@/screens/RequestLinkScreen';
import SetUpScreen from '@/screens/SetUpScreen';
import SuccessScreen from '@/screens/SuccessScreen';
import ManageScreen from '@/screens/ManageScreen';
import PendingBankVerificationScreen from '@/screens/PendingBankVerificationScreen';
import TurnOffConfirmScreen from '@/screens/TurnOffConfirmScreen';
import InvoiceRedirectScreen from '@/screens/InvoiceRedirectScreen';
import useHostedEntry from './useHostedEntry';
import { getAutoPayStatus, toggleAutoPay } from '@/services/api';
import { parseHostedError } from '@/services/errors';
import session from '@/session';

/**
 * Read + strip Stripe's 3DS-return query params, keeping ?t= intact. Both
 * the enrolment screen and the settings screen can send a new card through
 * a bank redirect; whichever the status lands on afterwards finishes the
 * sequence with this secret.
 */
const consumeResumeSecret = () => {
	const params = new URLSearchParams(window.location.search);
	const secret = params.get('setup_intent_client_secret');
	if (!secret) return null;
	['setup_intent', 'setup_intent_client_secret', 'redirect_status'].forEach((key) => params.delete(key));
	const search = params.toString();
	window.history.replaceState(null, '', window.location.pathname + (search ? `?${search}` : ''));
	return secret;
};

/**
 * The /auto-pay state machine. Steps are component state, NOT routes — the
 * link token must stay in the URL so an expired session can drop back to
 * verify, and deep-linking into (say) the success step is meaningless.
 */
const AutoPayFlow = () => {
	const entry = useHostedEntry();
	const [resumeSecret, setResumeSecret] = useState(consumeResumeSecret);

	const [step, setStep] = useState('loading'); // loading | setup | manage | success | turnoff | invoice | pending | dead | error
	const [pendingVerification, setPendingVerification] = useState(null);
	const [status, setStatus] = useState(null);
	const [loadError, setLoadError] = useState(null);
	const [turnOffBusy, setTurnOffBusy] = useState(false);
	const [turnOffError, setTurnOffError] = useState(null);
	// Shown on the settings screen after a method change has been saved and
	// the status re-read: the chip alone cannot show a change between two
	// identical cards.
	const [manageNotice, setManageNotice] = useState(null);

	const loadStatus = useCallback(async (goal = 'auto') => {
		setStep('loading');
		setLoadError(null);
		try {
			const fresh = await getAutoPayStatus(session.get().student_id);
			setStatus(fresh);

			if (!fresh.assignments.length) {
				setStep('dead');
				return null;
			}

			if (goal === 'success') setStep('success');
			else if (goal === 'manage') setStep('manage');
			// Auto-pay is one payer-level switch now — setup vs manage keys
			// off the global enrollment, never a per-plan flag. A pending ACH
			// verification shows the wait screen instead of re-enrolling.
			else if (fresh.auto_pay_enabled) setStep('manage');
			else if (fresh.auto_pay_pending) setStep('pending');
			else setStep('setup');
			return fresh;
		} catch (err) {
			const parsed = parseHostedError(err);
			if (parsed.type !== 'session') {
				setLoadError(parsed.message);
				setStep('error');
			}
			return null;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (entry.phase === 'ready') loadStatus(resumeSecret ? 'setup-resume' : 'auto');
	}, [entry.phase, loadStatus, resumeSecret]);

	// Amounts come from the CURRENTLY billing plan — auto-pay itself is one
	// payer-level switch, and the copy covers future plans generically.
	const assignment = status?.assignments.find((a) => !a.scheduled) ?? status?.assignments[0];
	// Context branding when we came through verify; status covers sessions
	// that never called the pre-auth context endpoint (webview hand-off,
	// refreshes with a live session).
	const school = entry.context?.school ?? status?.school;
	const schoolName = school?.name;
	// Full name from the authenticated status response; the pre-auth context
	// only carries the first name (the last name is the verify secret).
	const studentName = status?.student_name ?? entry.context?.student_first_name;

	const handleTurnOffConfirm = async () => {
		setTurnOffBusy(true);
		setTurnOffError(null);
		try {
			await toggleAutoPay({ student_id: status.student_id, enabled: false });
			await loadStatus('auto');
		} catch (err) {
			setTurnOffError(parseHostedError(err).message);
		} finally {
			setTurnOffBusy(false);
		}
	};

	const body = () => {
		if (entry.phase === 'request-link') return <RequestLinkScreen onBack={entry.closeRequestLink} />;
		if (entry.phase === 'dead') {
			return <DeadLinkScreen variant={entry.deadVariant} onRequestLink={entry.openRequestLink} />;
		}
		if (entry.phase === 'verify') {
			return (
				<VerifyScreen
					context={entry.context}
					onSubmit={entry.verify}
					error={entry.verifyError}
					busy={entry.verifyBusy}
					notice={entry.notice}
				/>
			);
		}
		if (entry.phase === 'loading' || step === 'loading') return <Spinner label="Loading…" />;

		if (step === 'dead') return <DeadLinkScreen variant="no_plans" />;
		if (step === 'error') {
			return (
				<div className="flex flex-col gap-[18px]">
					<ErrorBanner>{loadError}</ErrorBanner>
					<CtaButton onClick={() => loadStatus('auto')}>Try again</CtaButton>
				</div>
			);
		}
		if (step === 'pending') {
			return (
				<PendingBankVerificationScreen school={school} studentName={studentName} verification={pendingVerification} />
			);
		}
		if (step === 'invoice') {
			return (
				<InvoiceRedirectScreen
					studentId={status.student_id}
					studentName={studentName}
					onBack={() => setStep('setup')}
				/>
			);
		}
		if (step === 'success') {
			return (
				<SuccessScreen
					status={status}
					assignment={assignment}
					studentName={studentName}
					onManage={() => setStep('manage')}
				/>
			);
		}
		if (step === 'turnoff') {
			return (
				<TurnOffConfirmScreen
					schoolName={schoolName}
					onKeep={() => setStep('manage')}
					onConfirm={handleTurnOffConfirm}
					busy={turnOffBusy}
					error={turnOffError}
				/>
			);
		}
		if (step === 'manage') {
			return (
				<ManageScreen
					school={school}
					status={status}
					assignment={assignment}
					notice={manageNotice}
					onTurnOff={() => {
						setManageNotice(null);
						setTurnOffError(null);
						setStep('turnoff');
					}}
					onUpdated={() => {
						setManageNotice('Payment method updated.');
						loadStatus('manage');
					}}
					onNoticeDismiss={() => setManageNotice(null)}
					resumeClientSecret={resumeSecret}
					onResumeConsumed={() => setResumeSecret(null)}
					onPendingBank={() => {
						setManageNotice(
							'Bank account added. It becomes your Auto-Pay method once the two small test deposits are verified, usually within 1 to 2 business days.'
						);
						loadStatus('manage');
					}}
				/>
			);
		}

		return (
			<SetUpScreen
				school={school}
				status={status}
				assignment={assignment}
				studentName={studentName}
				onEnabled={() => loadStatus('success')}
				onPendingVerification={(verification) => {
					setPendingVerification(verification);
					setStep('pending');
				}}
				onPayInvoiceInstead={() => setStep('invoice')}
				resumeClientSecret={resumeSecret}
				onResumeConsumed={() => setResumeSecret(null)}
			/>
		);
	};

	return <CardShell>{body()}</CardShell>;
};

export default AutoPayFlow;
